/**
 * Is this session Beatly staff? The same GET /v1/me the staff bar reads (shared cache key). `null` while it
 * loads — callers treat that as "not yet", so staff-only parts appear a moment later rather than flash for a client.
 */
import { useQuery } from '@tanstack/react-query';
import { coworkApi } from '@/cowork/omniad/api/coworkApi';

/**
 * What one GET /v1/me query says about the role. An answer once given is kept: TanStack Query keeps the data of a
 * failed background refetch and sets isError beside it, and a client polled every minute must not turn into
 * "unknown" (and get the staff loaders, rail and guide) because one poll hit a restart. `failed` only when /v1/me
 * never answered.
 */
export function staffStateFrom(me: { data?: { staff?: boolean | null } | null; isPending: boolean; isError: boolean }): { staff: boolean | null; failed: boolean } {
  if (me.data) return { staff: me.data.staff === true, failed: false };
  if (me.isPending || me.isError) return { staff: null, failed: me.isError };
  return { staff: null, failed: false };
}

export function useStaffState(): { staff: boolean | null; failed: boolean } {
  const me = useQuery({ queryKey: ['cowork', 'me'], queryFn: () => coworkApi.staffScope.me(), staleTime: 5 * 60_000, retry: 2, retryDelay: (n) => 500 * (n + 1) });
  // Unknown until /v1/me answers; an error with no answer yet stays unknown (a staff member must not lose ?new=proposal to a blip).
  return staffStateFrom(me);
}

export function useIsStaff(): boolean | null {
  return useStaffState().staff;
}
